import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../grid/gridView';
import { WEB_COMPONENT_GRID_VIEW, WEB_COMPONENT_MDR_CUSTOMER_PANEL } from '../../../webcomponents.constants';
import { defaultPageSizeOptions } from '../../../grid/gridView/types';
import { MdrCompaniesGridViewDataProvider } from '../dataProvider/MdrCompaniesGridViewDataProvider';
import {
  Column,
  ColumnKeys,
  FILTER_TIME_RANGE,
  MdrCompaniesGridWebComponentSchema,
  MdrCompanyGridItem,
  PREDEFINED_FILTERS_KEYS,
} from '../types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { mdrDateRenderer, serviceStatusRenderer } from './customGridRenderers';
import { MdrCustomerPanelWebComponent } from '../../mdrCustomerPanel';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzInfoBoxWebComponent } from '../../../ui/gzInfoBox';

export class MdrCompaniesGridView extends BaseWebComponentView {
  dom: {
    $wrapper: HTMLElement;
    $title: GzTitleWebComponent;
    $infoBox: GzInfoBoxWebComponent;

    $gridWrapper: HTMLElement;
    $gridView: GridViewWebComponent<Column, ColumnKeys, MdrCompanyGridItem>;

    $customerWrapper: HTMLElement;
    $customerView: MdrCustomerPanelWebComponent;
    $openConsoleButton: GzButtonWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $title: this.shadow.querySelector('#page-title'),
      $infoBox: this.shadow.querySelector('#sso-disabled'),

      $gridWrapper: this.shadow.querySelector('.grid-wrapper'),
      $gridView: this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW),

      $customerWrapper: this.shadow.querySelector('.customer-wrapper'),
      $customerView: this.shadow.querySelector(WEB_COMPONENT_MDR_CUSTOMER_PANEL),
      $openConsoleButton: this.shadow.querySelector('#open-console-button'),
    };

    this.setLoadingState();
    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getMdrServiceStrings();
  }

  setTranslatedStrings() {
    this.dom.$title.innerText = this.translations.title;
    this.dom.$openConsoleButton.innerText = this.translations.openConsole;
    this.dom.$infoBox.innerText = this.translations.disabledSsoInfoBox;
  }

  buildSuccessView(data: MdrCompaniesGridWebComponentSchema) {
    if (Application.Main.canHaveChildCompanies()) {
      this.showCompaniesGrid(data);
    } else {
      this.showCustomerView(data);
    }

    this.dom.$openConsoleButton.classList.remove('hidden');

    this.setSso(data.ssoIsAvailable);

    this.removeLoadingState();
  }

  public setSso(enabled: boolean) {
    if (enabled) {
      this.enableSso();
    } else {
      this.disableSso();
    }
  }

  private enableSso() {
    this.dom.$openConsoleButton.disabled = false;
    this.dom.$infoBox.classList.add('hidden');
  }

  private disableSso() {
    this.dom.$openConsoleButton.disabled = true;
    this.dom.$infoBox.classList.remove('hidden');
  }

  private showCompaniesGrid(data: MdrCompaniesGridWebComponentSchema) {
    toggleVisibility(this.dom.$gridWrapper, true);
    toggleVisibility(this.dom.$customerWrapper, false);

    this.dom.$gridView.injectDependencies(this.dependencies);
    this.dom.$gridView.start().then().catch(console.error);

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.MDR_START_DATE]: mdrDateRenderer.bind(this),
      [ColumnKeys.MDR_END_DATE]: mdrDateRenderer.bind(this),
      [ColumnKeys.MDR_SERVICE_STATUS]: serviceStatusRenderer.bind(this),
    };

    this.dom.$gridView.sideMenuParent = this.shadow;

    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.viewId = data.view.id;
    this.dom.$gridView.dataProvider = new MdrCompaniesGridViewDataProvider(this.dependencies);
    this.dom.$gridView.allColumnDetails = data.columns;
    this.dom.$gridView.columns = data.view.displayedColumns;
    this.dom.$gridView.filters = data.view.filters;
    this.dom.$gridView.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$gridView.isCompactViewChecked = false;
    this.dom.$gridView.defaultViewProps = data.view;
    this.dom.$gridView.activeViewProps = data.view;
    this.dom.$gridView.activeViewInitialProps = data.view;
    this.dom.$gridView.pageSize = data.view.pageSize;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSizeOptions = defaultPageSizeOptions;
    this.dom.$gridView.enableCSVExport = false;
    this.dom.$gridView.hasSelectableRows = false;
    this.dom.$gridView.filtersUiConfigs = this.getFiltersUiConfig();

    this.dom.$gridView.propertiesSet = true;
  }

  private showCustomerView(data: MdrCompaniesGridWebComponentSchema) {
    toggleVisibility(this.dom.$customerWrapper, true);
    toggleVisibility(this.dom.$gridWrapper, false);

    if (this.dom.$customerView.injectDependencies) {
      this.dom.$customerView.injectDependencies(this.dependencies);
      this.dom.$customerView.start().then().catch(console.error);
    }

    if (this.dom.$customerView.started) {
      this.dom.$customerView.controller?.render();
      this.dom.$customerView.controller.view.setSso(data.ssoIsAvailable);
    }
  }

  private getFiltersUiConfig() {
    return [
      {
        key: ColumnKeys.MDR_START_DATE,
        config: {
          enableSelectAllToDate: true,
          activeRange: FILTER_TIME_RANGE,
          max: null,
        },
      },
      {
        key: ColumnKeys.MDR_END_DATE,
        config: {
          enableSelectAllToDate: true,
          activeRange: FILTER_TIME_RANGE,
          max: null,
        },
      },
    ];
  }
}
